import { cloneDeep } from "lodash";
import { useMemo } from "react";
import styles from "../aic.module.css";
import { useBlockContext } from "./BlockContentProvider";
import { UPDATE_BLOCK_CELL } from "./blockReducer";

interface CellEditorProps {
  aifid: string;
  position: "left" | "center" | "right";
}

/**
 * Editor wrapper for a each cell in a line.
 *
 * @param props - Destructured parameter object
 * @param props.aifid - The block instance id (`aifid`) identifying which line's cell to edit.
 * @param props.position - Cell position in the line: `"left"`, `"center"`, or `"right"`.
 */
export const CellEditor = <T,>({ aifid, position }: CellEditorProps): React.ReactElement => {
  const { state, dispatch } = useBlockContext<T>();
  const ix = state.lines.findIndex((l) => l.aifid === aifid);
  const thisLine = state.lines[ix];
  const thisCell = thisLine[position];
  const Editor = state.editorProps.Editor;

  // Update for replacements
  const displayValue = useMemo(() => {
    if (thisCell === undefined || thisCell === null) return undefined;
    let ret: T = cloneDeep(thisCell);
    state.externalSingles.forEach((repl) => {
      ret = state.editorProps.replaceTextInT(ret, repl.oldText, repl.newText);
    });
    return ret;
  }, [state.editorProps, state.externalSingles, thisCell]);

  const editable = !state.disabled && thisLine?.canEdit;

  return (
    <Editor
      id={`${state.id}-${ix}-${position}-text`}
      className={!editable ? styles.aibReadOnly : ""}
      editable={editable}
      preReplacedText={thisCell}
      setValue={(ret) =>
        dispatch({ operation: UPDATE_BLOCK_CELL, aifid, position, cellContent: ret })
      }
      textAlignment={position}
      value={displayValue ?? state.editorProps.blankT}
      commentConfig={
        state.noComments
          ? undefined
          : {
              id: aifid,
              activeContext: "TlfItem",
              label: "New line comment",
              iconPosition: { top: "-2px", left: "-2px" },
            }
      }
    />
  );
};

CellEditor.displayName = "CellEditor";
